import React from 'react'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
// @src imports
import { getTotal } from 'src/helpers'
import {
  Confirm,
  DefaultError,
  EmptyState,
  ErrorBoundary,
  Search,
  Transition,
} from 'src/chrome'
import { ContextFooter } from 'src/orders/components'
import NotYourCard from 'src/assets/svg/NotYourCard'
import { GiftRoute } from 'src/gift_store/routes/GiftRoute'
import { GiftCategoryRoute } from 'src/gift_store/routes/GiftCategoryRoute'
import suspenseBoundary from 'src/chrome/SuspenseBoundary/suspenseBoundaryHOC'
import {
  CampaignFragment,
  getGiftCategories,
  GiftFragment,
  GiftVariationFragment,
} from 'src/graphql'
import { GiftStoreRoute } from 'src/gift_store/routes/GiftStoreRoute'
import { CAMPAIGN_CONTEXT } from 'src/context/types'
import { GiftModal, GiftRibbon } from 'src/gift_store/components'
// relative imports
import styles from './giftStore.module.scss'
import {
  useActions,
  useAppcues,
  useEffect,
  useMutations,
  useQueries,
  useSelector,
  useState,
} from 'src/hooks'
import TabsContent from '../components/Tabs/Tabs'
import { Text } from '@sendoutcards/quantum-design-ui'
import AnnouncementBanners from 'src/marketing/AnnouncementBanners/AnnouncementBanners'

type GiftStoreProps = {
  route: GiftStoreRoute
}

enum Steps {
  Idle,
  ViewGift,
}

interface IdleStep {
  type: Steps.Idle
}

interface ViewGiftStep {
  type: Steps.ViewGift
  slug: string
}

type Step = IdleStep | ViewGiftStep

const GiftStore: React.FC<GiftStoreProps> = props => {
  const { route } = props

  const actions = useActions()
  const mutations = useMutations()

  const initialGiftSlug = (() => {
    if (route?.subroute?.path === GiftRoute.path) {
      const slug = route.subroute.giftId
      if (slug) return slug
    }

    return undefined
  })()

  const [categories] = useQueries(getGiftCategories())
  const orders = useSelector(state => state.orders)
  const { isMobile } = useSelector(state => state.window)

  const highLevelCategory = route.highLevelCategory || categories[0].id

  const [searchTerm, setSearchTerm] = useState<string>()

  const [step, setStep] = useState<Step>(
    initialGiftSlug
      ? { type: Steps.ViewGift, slug: initialGiftSlug }
      : { type: Steps.Idle },
  )

  const resetStep = () => setStep({ type: Steps.Idle })
  const context = useSelector(state => state.context)

  const [addingGift, setAddingGift] = useState<{
    status: boolean
    message: string
  }>({ status: false, message: '' })

  const [confirm, setConfirm] = useState<{
    title: string
    message: string
    action: () => void
  }>()

  const limit = 200

  useAppcues('-L8hSjiwftl1lOHA3uw7')

  const closeConfirmModal = () => {
    setConfirm(undefined)
  }

  const createGiftOrder = async (giftId: string) => {
    /* create order and go to the order page */
    const {
      createOrder: { order },
    } = await mutations.createOrder({
      order: { lines: [{ giftVariation: giftId }] },
    })
    actions.createdOrder(order)
    actions.openOrder(order.id)
  }

  const viewGift = (slug: string) => {
    setStep({ type: Steps.ViewGift, slug })
  }

  // for now this is used to update the URL
  useEffect(() => {
    if (step.type === Steps.ViewGift) {
      actions.selectGiftToView(step.slug)
      return
    }
    actions.closeGiftBeingViewed()
  }, [step, actions])

  const addGift = (gift: GiftFragment) => {
    const selectedGift = gift && gift.defaultVariation

    if (!selectedGift) {
      return
    }

    if (!invalidOrderState()) {
      setAddingGift({ status: true, message: 'Adding gift to order..' })
      localSelectGift(selectedGift)
    } else {
      setConfirm({
        title: "Can't add gift",
        message:
          'Cannot send a gift with a postcard. Would you like to create a new order for this gift?',
        action: () => {
          actions.clearOrder()
          createGiftOrder(selectedGift.id)
        },
      })
    }
  }

  const invalidOrderState = () => {
    return (
      orders.order &&
      orders.activeLine &&
      orders.activeLine.card &&
      orders.activeLine.card.type === 'POSTCARD'
    )
  }

  const updateCampaign = async (campaign: CampaignFragment) => {
    await mutations.updateCampaign({
      campaign: {
        id: campaign.id,
        name: campaign.name,
        lines: campaign.lines?.map(line => {
          return {
            card: line.card?.id,
            giftVariation: line.giftVariation?.id,
            sendDelay: line.sendDelay,
          }
        }),
      },
    })
    actions.editedCampaign(campaign.id)
    actions.openCampaign(campaign.id)
  }

  const localSelectGift = (gift: GiftVariationFragment) => {
    actions.selectGift(gift.id)

    if (context.kind === CAMPAIGN_CONTEXT && context.line !== undefined) {
      updateCampaign({
        ...context.campaign,
        lines: context.campaign.lines.map((line, index) => {
          if (index === context.line) {
            return {
              ...line,
              giftVariation: gift,
            }
          }
          return line
        }),
      })
    } else if (orders.order) {
      const containerId = orders.order.id
      containerId ? actions.updateGift(gift) : actions.saveGift(gift)
      setTimeout(() => actions.openOrder(containerId), 500)
    } else {
      createGiftOrder(gift.id)
    }
  }

  const selectCategory = (id: string) => {
    actions.selectChildCategory(id)
  }

  return (
    <div className={styles.mainContainer}>
      <AnnouncementBanners />
      <div className={styles.header}>
        <Search
          id="gift_store_search"
          style={{ top: 0, paddingBottom: 0 }}
          onSearch={setSearchTerm}
          value={searchTerm}
          autocomplete={'off'}
          clearSearch={() => setSearchTerm('')}
        />
        {!isMobile && (
          <TabsContent
            value={highLevelCategory}
            onChange={categoryId => {
              if (highLevelCategory !== categoryId) {
                actions.selectHighLevelCategory(categoryId)
                setSearchTerm(undefined)
              }
            }}
          />
        )}
        {isMobile && (
          <Select
            value={highLevelCategory}
            onChange={e => {
              if (highLevelCategory !== e.target.value) {
                actions.selectHighLevelCategory(e.target.value as string)
                setSearchTerm(undefined)
              }
            }}
            style={{ width: '100%' }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {categories.map(category => {
              return (
                <MenuItem value={category.id} key={category.id}>
                  <Text type="body">{category.name}</Text>
                </MenuItem>
              )
            })}
          </Select>
        )}
      </div>
      {addingGift.status && <Transition message={addingGift.message} />}
      {!addingGift.status && (
        <div className={styles.scrollableContainer}>
          {searchTerm ? (
            <GiftRibbon
              shouldWrapContent={true}
              name="Search Results"
              search={searchTerm}
              giftLimit={limit}
              viewGift={viewGift}
              addGift={addGift}
            />
          ) : (
            categories.map(category => {
              if (highLevelCategory === category.id) {
                return (category.children ?? [])
                  .filter(
                    child =>
                      !route.subroute ||
                      route.subroute.path === GiftRoute.path ||
                      (route.subroute.path === GiftCategoryRoute.path &&
                        child.id === route.subroute.giftCategoryId),
                  )
                  .map((child, index) => (
                    <GiftRibbon
                      id={index}
                      shouldWrapContent={
                        route.subroute &&
                        route.subroute.path === GiftCategoryRoute.path
                      }
                      name={child.name}
                      categoryId={child.id}
                      onSelect={() => selectCategory(child.id)}
                      key={child.id}
                      giftLimit={limit}
                      viewGift={viewGift}
                      addGift={addGift}
                    />
                  ))
              } else {
                return null
              }
            })
          )}
          {orders.order && (
            <ContextFooter
              cards={orders.order.lines.length}
              total={getTotal(orders.order.cost)}
              isInCatalog={true}
              footerType={'order'}
              name={orders.order.id}
              cancelContext={actions.openOrder.bind(null, orders.order.id)}
            />
          )}
          {context.kind === 'CAMPAIGN_CONTEXT' && (
            <ContextFooter
              cards={context.campaign.lines.length}
              total={null}
              isInCatalog={true}
              footerType={'campaign'}
              name={context.campaign.name}
              cancelContext={() => actions.openCampaign(context.campaign.id)}
            />
          )}
        </div>
      )}
      {step.type === Steps.ViewGift && (
        <ErrorBoundary
          fallback={error => (
            <EmptyState
              action={() => actions.openGiftStore()}
              actionTitle={'Find a new gift'}
              message={"Ooops, it looks like this gift doesn't exist"}
              artWorkWidth={'70%'}
              artWorkMaxWidth={600}
            >
              <NotYourCard />
            </EmptyState>
          )}
        >
          <GiftModal
            key={'gift_modal'}
            slug={step.slug}
            onClose={resetStep}
            addGift={addGift}
          />
        </ErrorBoundary>
      )}
      {confirm && (
        <Confirm
          title={confirm.title}
          message={confirm.message}
          onDecline={closeConfirmModal}
          onConfirm={confirm.action}
        />
      )}
    </div>
  )
}

export default suspenseBoundary({
  component: GiftStore,
  failure: DefaultError,
  unresolved: <Transition message="Loading gift store..." />,
})
